import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { adminAPI, employerAPI, sessionAPI, applicationAPI, donorRequestAPI, eventAPI, uploadAPI } from '../../services/api';
import { 
    FaUsers, FaBriefcase, FaFileAlt, FaCalendarAlt, 
    FaChartLine, FaCog, FaPlus, FaEye, FaUserShield, FaBuilding,
    FaBell, FaExclamationTriangle, FaTh, FaArrowRight, FaHandshake, FaHeart,
    FaDownload, FaEdit, FaTrash
} from 'react-icons/fa';
import {
    DashboardLayout,
    StatCard,
    StatusBadge,
    LoadingSpinner,
    EmptyState
} from '../../components/dashboard';

const AdminDashboard = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const tabFromUrl = searchParams.get('tab') || 'dashboard';
    const [activeTab, setActiveTab] = useState(tabFromUrl);
    const [stats, setStats] = useState({
        users: {},
        applications: {},
        employers: {},
        jobs: {}
    });
    const [trends, setTrends] = useState({
        users: 0,
        applications: 0,
        employers: 0,
        jobs: 0
    });
    const [pendingEmployers, setPendingEmployers] = useState([]);
    const [recentUsers, setRecentUsers] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [allSessions, setAllSessions] = useState([]);
    const [allEmployers, setAllEmployers] = useState([]);
    const [allApplications, setAllApplications] = useState([]);
    const [activeSessions, setActiveSessions] = useState([]);
    const [donorRequests, setDonorRequests] = useState([]);
    const [events, setEvents] = useState([]);
    const [eventForm, setEventForm] = useState({
        title: '',
        description: '',
        content: '',
        event_date: '',
        event_location: '',
        media_type: 'none',
        media_url: '',
        banner_url: '',
        gallery_images: [],
        video_url: '',
        is_published: true
    });
    const [eventFile, setEventFile] = useState(null);
    const [eventBannerFile, setEventBannerFile] = useState(null);
    const [eventGalleryFiles, setEventGalleryFiles] = useState([]);
    const [editingEventId, setEditingEventId] = useState(null);
    const [savingEvent, setSavingEvent] = useState(false);
    const [loading, setLoading] = useState(true);
    const [tabLoading, setTabLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [applicationFilters, setApplicationFilters] = useState({
        status: 'all',
        village: 'all'
    });

    // Use ref to prevent duplicate calls
    const dataLoadedRef = useRef(false);
    const isLoadingRef = useRef(false);
    
    useEffect(() => {
        // Prevent duplicate calls
        if (dataLoadedRef.current || isLoadingRef.current) {
            return;
        }
        
        isLoadingRef.current = true;
        dataLoadedRef.current = true;
        
        loadDashboardData().finally(() => {
            isLoadingRef.current = false;
        });
        
        // Set up interval for periodic refresh (every 30 seconds)
        const interval = setInterval(() => {
            if (!isLoadingRef.current) {
                isLoadingRef.current = true;
                loadDashboardData().finally(() => {
                    isLoadingRef.current = false;
                });
            }
        }, 30000);
        
        return () => {
            clearInterval(interval);
            // Don't reset dataLoadedRef - we want it to stay true
        };
    }, []); // Empty deps - only run once on mount, use interval for refresh

    useEffect(() => {
        // Sync URL parameter with active tab on mount
        const tabFromUrl = searchParams.get('tab') || 'dashboard';
        if (tabFromUrl !== activeTab) {
            setActiveTab(tabFromUrl);
        }
    }, [searchParams, activeTab]);

    useEffect(() => {
        if (activeTab !== 'dashboard') {
            loadTabData();
        }
    }, [activeTab]);

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setSearchParams({ tab });
    };

    const loadDashboardData = async () => {
        try {
            const [statsRes, employersRes, sessionsRes, usersRes] = await Promise.all([
                adminAPI.getDashboardStats().catch(err => {
                    console.error('Failed to load stats:', err);
                    return { stats: {} };
                }),
                employerAPI.getAll({ is_approved: false }).catch(err => {
                    console.error('Failed to load employers:', err);
                    return { employers: [] };
                }),
                sessionAPI.getAll({ status: 'active' }).catch(err => {
                    console.error('Failed to load sessions:', err);
                    return { sessions: [] };
                }),
                adminAPI.getAllUsers({ limit: 5 }).catch(err => {
                    console.error('Failed to load users:', err);
                    return { users: [] };
                })
            ]);

            setStats(statsRes.stats || {});
            setPendingEmployers(prev => {
                const newEmployers = employersRes.employers || [];
                const prevStr = JSON.stringify(prev);
                const newStr = JSON.stringify(newEmployers);
                return prevStr === newStr ? prev : newEmployers;
            });
            setActiveSessions(prev => {
                const newSessions = sessionsRes.sessions || [];
                const prevStr = JSON.stringify(prev);
                const newStr = JSON.stringify(newSessions);
                return prevStr === newStr ? prev : newSessions;
            });
            setRecentUsers(prev => {
                const newUsers = usersRes.users || [];
                const prevStr = JSON.stringify(prev);
                const newStr = JSON.stringify(newUsers);
                return prevStr === newStr ? prev : newUsers;
            });

            const calculateTrend = (current) => {
                const previous = current * 0.9;
                if (current === 0) return 0;
                return ((current - previous) / previous) * 100;
            };

            const totalUsers = getTotalUsers();
            const totalApplications = getTotalApplications();
            const totalEmployers = getTotalEmployers();
            const totalJobs = statsRes.stats?.jobs?.total || 0;

            setTrends({
                users: calculateTrend(totalUsers),
                applications: calculateTrend(totalApplications),
                employers: calculateTrend(totalEmployers),
                jobs: calculateTrend(totalJobs)
            });
        } catch (err) {
            console.error('Failed to load dashboard:', err);
        } finally {
            setLoading(false);
        }
    };

    const loadTabData = async () => {
        setTabLoading(true);
        try {
            if (activeTab === 'users') {
                const res = await adminAPI.getAllUsers({ limit: 50 });
                setAllUsers(res.users || []);
            } else if (activeTab === 'sessions') {
                const res = await sessionAPI.getAll();
                setAllSessions(res.sessions || []);
            } else if (activeTab === 'employers') {
                const res = await employerAPI.getAll();
                setAllEmployers(res.employers || []);
            } else if (activeTab === 'applications') {
                const sessionsRes = await sessionAPI.getAll();
                const sessions = sessionsRes.sessions || [];
                let apps = [];
                for (const session of sessions) {
                    try {
                        const res = await applicationAPI.getBySession(session.id);
                        apps = [...apps, ...(res.applications || [])];
                    } catch (err) {
                        console.error(`Failed to load applications for session ${session.id}:`, err);
                    }
                }
                setAllApplications(apps);
            } else if (activeTab === 'donor-requests') {
                const res = await donorRequestAPI.getAll();
                setDonorRequests(res.requests || []);
            } else if (activeTab === 'events') {
                const res = await eventAPI.getAllAdmin();
                setEvents(res.events || []);
            }
        } catch (err) {
            console.error('Failed to load tab data:', err);
        } finally {
            setTabLoading(false);
        }
    };

    const getTotalUsers = () => {
        if (!stats.users || !Array.isArray(stats.users)) return 0;
        return stats.users.reduce((sum, u) => sum + (u.count || 0), 0);
    };

    const getTotalApplications = () => {
        if (!stats.applications || !Array.isArray(stats.applications)) return 0;
        return stats.applications.reduce((sum, a) => sum + (a.count || 0), 0);
    };

    const getTotalEmployers = () => {
        if (!stats.employers || !Array.isArray(stats.employers)) return 0;
        return stats.employers.reduce((sum, e) => sum + (e.count || 0), 0);
    };

    const getPendingApplications = () => {
        if (!stats.applications || !Array.isArray(stats.applications)) return 0;
        const pending = stats.applications.find(a => a.status === 'submitted' || a.status === 'under_review');
        return pending ? pending.count : 0;
    };

    const tabs = [
        { id: 'dashboard', label: 'Dashboard Overview', icon: FaTh },
        { id: 'users', label: 'User Management', icon: FaUsers },
        { id: 'sessions', label: 'Session Management', icon: FaCalendarAlt },
        { id: 'employers', label: 'Employer Management', icon: FaBuilding },
        { id: 'applications', label: 'Applications', icon: FaFileAlt },
        { id: 'donor-requests', label: 'Donor Requests', icon: FaHeart },
        { id: 'events', label: 'News & Events', icon: FaBell },
        { id: 'reports', label: 'Reports & Analytics', icon: FaChartLine },
        { id: 'settings', label: 'System Settings', icon: FaCog }
    ];

    const resetEventForm = () => {
        setEventForm({
            title: '',
            description: '',
            content: '',
            event_date: '',
            event_location: '',
            media_type: 'none',
            media_url: '',
            banner_url: '',
            gallery_images: [],
            video_url: '',
            is_published: true
        });
        setEventFile(null);
        setEventBannerFile(null);
        setEventGalleryFiles([]);
        setEditingEventId(null);
    };

    const handleEditEvent = (event) => {
        setEditingEventId(event.id);
        setEventForm({
            title: event.title || '',
            description: event.description || '',
            content: event.content || '',
            event_date: event.event_date ? new Date(event.event_date).toISOString().slice(0, 10) : '',
            event_location: event.event_location || '',
            media_type: event.media_type || 'none',
            media_url: event.media_url || '',
            banner_url: event.banner_url || '',
            gallery_images: Array.isArray(event.gallery_images) ? event.gallery_images : [],
            video_url: event.video_url || '',
            is_published: Boolean(event.is_published)
        });
        setEventFile(null);
        setEventBannerFile(null);
        setEventGalleryFiles([]);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSubmitEvent = async (e) => {
        e.preventDefault();
        setSavingEvent(true);
        try {
            let mediaUrl = eventForm.media_url;
            let mediaType = eventForm.media_type;

            if (eventFile) {
                const uploadRes = await uploadAPI.uploadFile(eventFile);
                mediaUrl = uploadRes?.file?.path || '';
                if (eventFile.type.startsWith('video/')) {
                    mediaType = 'video';
                } else if (eventFile.type.startsWith('image/')) {
                    mediaType = 'image';
                }
            }

            if (mediaType !== 'none' && !String(mediaUrl || '').trim()) {
                toast.error('Please upload intro media file or choose "No media"');
                return;
            }

            let bannerUrl = (eventForm.banner_url || '').trim();
            if (eventBannerFile) {
                const bannerUpload = await uploadAPI.uploadFile(eventBannerFile);
                bannerUrl = bannerUpload?.file?.path || '';
            }

            let galleryImages = Array.isArray(eventForm.gallery_images) ? [...eventForm.gallery_images] : [];
            if (eventGalleryFiles.length > 0) {
                const galleryUpload = await uploadAPI.uploadFiles(eventGalleryFiles);
                const uploaded = Array.isArray(galleryUpload?.files)
                    ? galleryUpload.files.map((file) => file?.path).filter(Boolean)
                    : [];
                galleryImages = [...galleryImages, ...uploaded];
            }

            const payload = {
                ...eventForm,
                media_type: mediaType,
                media_url: mediaType === 'none' ? '' : mediaUrl,
                banner_url: bannerUrl,
                gallery_images: galleryImages,
                is_published: eventForm.is_published ? 1 : 0
            };

            if (editingEventId) {
                await eventAPI.update(editingEventId, payload);
                toast.success('Event updated successfully');
            } else {
                await eventAPI.create(payload);
                toast.success('Event created successfully');
            }

            resetEventForm();
            const updated = await eventAPI.getAllAdmin();
            setEvents(updated.events || []);
        } catch (error) {
            toast.error(error.message || 'Failed to save event');
        } finally {
            setSavingEvent(false);
        }
    };

    const handleDeleteEvent = async (eventId) => {
        if (!window.confirm('Delete this event? This action cannot be undone.')) {
            return;
        }
        try {
            await eventAPI.remove(eventId);
            setEvents((prev) => prev.filter((event) => event.id !== eventId));
            if (editingEventId === eventId) {
                resetEventForm();
            }
            toast.success('Event deleted');
        } catch (error) {
            toast.error(error.message || 'Failed to delete event');
        }
    };

    const handleGalleryFileSelection = (e) => {
        const selectedFiles = Array.from(e.target.files || []);
        if (selectedFiles.length === 0) return;

        setEventGalleryFiles((prev) => {
            const merged = [...prev];
            selectedFiles.forEach((file) => {
                const exists = merged.some(
                    (existing) =>
                        existing.name === file.name &&
                        existing.size === file.size &&
                        existing.lastModified === file.lastModified
                );
                if (!exists) merged.push(file);
            });
            return merged;
        });

        // Reset input so selecting same file again still triggers change event
        e.target.value = '';
    };

    const removePendingGalleryFile = (indexToRemove) => {
        setEventGalleryFiles((prev) => prev.filter((_, index) => index !== indexToRemove));
    };

    // Get unique villages from applications
    const uniqueVillages = Array.from(new Set(
        allApplications
            .map(app => app.address_village)
            .filter(v => v && v.trim() !== '')
    )).sort();

    // Filter applications based on search query and filters
    const filteredApplications = allApplications.filter(app => {
        // Search filter
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            const matchesSearch = (
                app.first_name?.toLowerCase().includes(query) ||
                app.last_name?.toLowerCase().includes(query) ||
                app.email?.toLowerCase().includes(query) ||
                app.session_name?.toLowerCase().includes(query) ||
                app.address_village?.toLowerCase().includes(query)
            );
            if (!matchesSearch) return false;
        }

        // Status filter
        if (applicationFilters.status !== 'all') {
            if (applicationFilters.status === 'pending') {
                if (!['submitted', 'under_review'].includes(app.status)) return false;
            } else if (app.status !== applicationFilters.status) {
                return false;
            }
        }

        // Village filter
        if (applicationFilters.village !== 'all') {
            if (app.address_village !== applicationFilters.village) return false;
        }

        return true;
    });

    // CSV Export Function
    const exportToCSV = () => {
        if (filteredApplications.length === 0) {
            toast.error('No applications to export');
            return;
        }

        // Define CSV headers
        const headers = [
            'ID',
            'First Name',
            'Last Name',
            'Email',
            'Contact Number',
            'Date of Birth',
            'Gender',
            'Village',
            'City',
            'State',
            'Country',
            'Highest Qualification',
            'Institution Name',
            'Year of Passing',
            'Current Occupation',
            'Session Name',
            'Stage',
            'Status',
            'Submitted At',
            'Created At'
        ];

        // Convert applications to CSV rows
        const rows = filteredApplications.map(app => [
            app.id || '',
            app.first_name || '',
            app.last_name || '',
            app.email || '',
            app.contact_number || '',
            app.date_of_birth ? new Date(app.date_of_birth).toLocaleDateString() : '',
            app.gender || '',
            app.address_village || '',
            app.address_city || '',
            app.address_state || '',
            app.address_country || '',
            app.highest_qualification || '',
            app.institution_name || '',
            app.year_of_passing || '',
            app.current_occupation || '',
            app.session_name || '',
            app.stage || '',
            app.status || '',
            app.submitted_at ? new Date(app.submitted_at).toLocaleDateString() : '',
            app.created_at ? new Date(app.created_at).toLocaleDateString() : ''
        ]);

        // Combine headers and rows
        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(cell => {
                // Escape commas and quotes in cell values
                const cellStr = String(cell || '');
                if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
                    return `"${cellStr.replace(/"/g, '""')}"`;
                }
                return cellStr;
            }).join(','))
        ].join('\n');

        // Create blob and download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `student_applications_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        toast.success(`Exported ${filteredApplications.length} applications to CSV`);
    };

    if (loading) {
        return (
            <DashboardLayout
                tabs={tabs}
                activeTab={activeTab}
                onTabChange={handleTabChange}
                headerTitle="Admin Dashboard"
                headerSubtitle="Manage users, sessions, employers, and system settings"
                headerIcon={FaUserShield}
                headerIconBgColor="bg-red-50"
                headerIconColor="text-red-600"
                showSearch={activeTab === 'applications'}
                searchValue={searchQuery}
                onSearchChange={setSearchQuery}
                badgeCounts={{ applications: allApplications.length }}
            >
                <LoadingSpinner />
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            headerTitle="Admin Dashboard"
            headerSubtitle="Manage users, sessions, employers, and system settings"
            headerIcon={FaUserShield}
            headerIconBgColor="bg-red-50"
            headerIconColor="text-red-600"
            showSearch={activeTab === 'applications'}
            searchValue={searchQuery}
            onSearchChange={setSearchQuery}
            badgeCounts={{ applications: allApplications.length }}
        >

                {activeTab === 'dashboard' && (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                            <StatCard
                                title="Total Users"
                                value={getTotalUsers()}
                                trend={trends.users}
                                icon={FaUsers}
                                iconColor="bg-blue-50 text-blue-600"
                            />
                            <StatCard
                                title="Applications"
                                value={getTotalApplications()}
                                trend={trends.applications}
                                subtitle={`${getPendingApplications()} Pending Review`}
                                icon={FaFileAlt}
                                iconColor="bg-green-50 text-green-600"
                            />
                            <StatCard
                                title="Employers"
                                value={getTotalEmployers()}
                                trend={trends.employers}
                                subtitle={pendingEmployers.length > 0 ? `${pendingEmployers.length} Pending Approval` : undefined}
                                icon={FaBuilding}
                                iconColor="bg-purple-50 text-purple-600"
                            />
                            <StatCard
                                title="Job Postings"
                                value={stats.jobs?.total || 0}
                                trend={trends.jobs}
                                subtitle={`${stats.jobs?.total_applications || 0} Total Applications`}
                                icon={FaBriefcase}
                                iconColor="bg-yellow-50 text-yellow-600"
                            />
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {pendingEmployers.length > 0 && (
                                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                                    <h2 className="text-xl font-bold text-gray-900 mb-4">Pending Employer Approvals</h2>
                                    <div className="space-y-3">
                                        {pendingEmployers.slice(0, 5).map((employer) => (
                                            <Link
                                                key={employer.id}
                                                to={`/admin/employers/${employer.id}/review`}
                                                className="block border-b border-gray-200 pb-3 hover:bg-gray-50 p-2 rounded transition"
                                            >
                                                <div className="flex justify-between items-start">
                                                    <div>
                                                        <span className="font-medium text-gray-900">{employer.company_name}</span>
                                                        <span className="text-gray-600 text-sm ml-2 block">{employer.email}</span>
                                                    </div>
                                                    <StatusBadge status={employer.compliance_status || 'pending'} />
                                                </div>
                                            </Link>
                                        ))}
                                        {pendingEmployers.length > 5 && (
                                            <Link
                                                to="/admin/employers"
                                                className="block text-center text-blue-600 hover:text-blue-700 font-medium text-sm py-2"
                                            >
                                                View All ({pendingEmployers.length}) <FaArrowRight className="inline ml-1" />
                                            </Link>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                                <h2 className="text-xl font-bold text-gray-900 mb-4">Recent Users</h2>
                                {recentUsers.length === 0 ? (
                                    <EmptyState
                                        message="No recent users"
                                    />
                                ) : (
                                    <div className="space-y-3">
                                        {recentUsers.map((user) => (
                                            <div
                                                key={user.id}
                                                className="block border-b border-gray-200 pb-3 hover:bg-gray-50 p-2 rounded transition"
                                            >
                                                <div className="flex justify-between items-start">
                                                    <div>
                                                        <span className="font-medium text-gray-900">
                                                            {user.first_name} {user.surname}
                                                        </span>
                                                        <span className="text-gray-600 text-sm ml-2 block">{user.email}</span>
                                                    </div>
                                                    <StatusBadge status={user.role} />
                                                </div>
                                            </div>
                                        ))}
                                        <Link
                                            to="/admin/users"
                                            className="block text-center text-blue-600 hover:text-blue-700 font-medium text-sm py-2"
                                        >
                                            View All Users <FaArrowRight className="inline ml-1" />
                                        </Link>
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}

                {activeTab === 'users' && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-bold text-gray-900">User Management</h2>
                            <Link
                                to="/admin/users"
                                className="text-blue-600 hover:text-blue-700 font-medium text-sm"
                            >
                                Manage Users <FaArrowRight className="inline ml-1" />
                            </Link>
                        </div>
                        {tabLoading ? (
                            <div className="text-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
                                <p className="text-gray-600">Loading users...</p>
                            </div>
                        ) : allUsers.length === 0 ? (
                            <EmptyState
                                icon={FaUsers}
                                message="No users found"
                            />
                        ) : (
                            <div className="space-y-3">
                                {allUsers.map((user) => (
                                    <div
                                        key={user.id}
                                        className="block border-b border-gray-200 pb-3 hover:bg-gray-50 p-2 rounded transition"
                                    >
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <span className="font-medium text-gray-900">
                                                    {user.first_name} {user.surname}
                                                </span>
                                                <span className="text-gray-600 text-sm ml-2 block">{user.email}</span>
                                                <span className="text-gray-500 text-xs ml-2 block">
                                                    Created: {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                                                </span>
                                            </div>
                                            <StatusBadge status={user.role} />
                                        </div>
                                    </div>
                                ))}
                                <Link
                                    to="/admin/users"
                                    className="block text-center text-blue-600 hover:text-blue-700 font-medium text-sm py-2"
                                >
                                    View All Users ({allUsers.length}) <FaArrowRight className="inline ml-1" />
                                </Link>
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'sessions' && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-bold text-gray-900">Session Management</h2>
                            <Link
                                to="/admin/sessions/create"
                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
                            >
                                <FaPlus /> Create Session
                            </Link>
                        </div>
                        {tabLoading ? (
                            <div className="text-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
                                <p className="text-gray-600">Loading sessions...</p>
                            </div>
                        ) : allSessions.length === 0 ? (
                            <EmptyState
                                icon={FaCalendarAlt}
                                message="No sessions found"
                            />
                        ) : (
                            <div className="space-y-3">
                                {allSessions.map((session) => (
                                    <Link
                                        key={session.id}
                                        to={`/admin/sessions/${session.id}`}
                                        className="block border-b border-gray-200 pb-3 hover:bg-gray-50 p-2 rounded transition"
                                    >
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <span className="font-medium text-gray-900">{session.name}</span>
                                                <span className="text-gray-600 text-sm ml-2 block">
                                                    {session.start_date && new Date(session.start_date).toLocaleDateString()} - 
                                                    {session.end_date && new Date(session.end_date).toLocaleDateString()}
                                                </span>
                                                {session.description && (
                                                    <span className="text-gray-500 text-xs ml-2 block">{session.description}</span>
                                                )}
                                            </div>
                                            <StatusBadge status={session.status || 'active'} />
                                        </div>
                                    </Link>
                                ))}
                                <Link
                                    to="/admin/sessions"
                                    className="block text-center text-blue-600 hover:text-blue-700 font-medium text-sm py-2"
                                >
                                    View All Sessions <FaArrowRight className="inline ml-1" />
                                </Link>
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'employers' && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-bold text-gray-900">Employer Management</h2>
                            <Link
                                to="/admin/employers"
                                className="text-blue-600 hover:text-blue-700 font-medium text-sm"
                            >
                                Manage Employers <FaArrowRight className="inline ml-1" />
                            </Link>
                        </div>
                        {tabLoading ? (
                            <div className="text-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
                                <p className="text-gray-600">Loading employers...</p>
                            </div>
                        ) : allEmployers.length === 0 ? (
                            <EmptyState
                                icon={FaBuilding}
                                message="No employers found"
                            />
                        ) : (
                            <div className="space-y-3">
                                {allEmployers.map((employer) => (
                                    <Link
                                        key={employer.id}
                                        to={`/admin/employers/${employer.id}/review`}
                                        className="block border-b border-gray-200 pb-3 hover:bg-gray-50 p-2 rounded transition"
                                    >
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <span className="font-medium text-gray-900">{employer.company_name}</span>
                                                <span className="text-gray-600 text-sm ml-2 block">{employer.email}</span>
                                                {employer.company_description && (
                                                    <span className="text-gray-500 text-xs ml-2 block line-clamp-1">
                                                        {employer.company_description}
                                                    </span>
                                                )}
                                            </div>
                                            <StatusBadge status={employer.is_approved ? 'approved' : 'pending'} />
                                        </div>
                                    </Link>
                                ))}
                                <Link
                                    to="/admin/employers"
                                    className="block text-center text-blue-600 hover:text-blue-700 font-medium text-sm py-2"
                                >
                                    View All Employers ({allEmployers.length}) <FaArrowRight className="inline ml-1" />
                                </Link>
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'applications' && (
                    <div className="space-y-6">
                        {/* Statistics Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-lg p-6 text-white">
                                <div className="flex items-center justify-between mb-2">
                                    <FaFileAlt className="text-3xl opacity-80" />
                                    <span className="text-sm opacity-90">Total</span>
                                </div>
                                <div className="text-4xl font-bold mb-1">{allApplications.length}</div>
                                <div className="text-sm opacity-90">Student Applications</div>
                            </div>
                            <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-lg shadow-lg p-6 text-white">
                                <div className="flex items-center justify-between mb-2">
                                    <FaFileAlt className="text-3xl opacity-80" />
                                    <span className="text-sm opacity-90">Pending</span>
                                </div>
                                <div className="text-4xl font-bold mb-1">
                                    {allApplications.filter(a => a.status === 'submitted' || a.status === 'under_review').length}
                                </div>
                                <div className="text-sm opacity-90">Under Review</div>
                            </div>
                            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-lg p-6 text-white">
                                <div className="flex items-center justify-between mb-2">
                                    <FaFileAlt className="text-3xl opacity-80" />
                                    <span className="text-sm opacity-90">Selected</span>
                                </div>
                                <div className="text-4xl font-bold mb-1">
                                    {allApplications.filter(a => a.status === 'selected').length}
                                </div>
                                <div className="text-sm opacity-90">Accepted</div>
                            </div>
                            <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-lg shadow-lg p-6 text-white">
                                <div className="flex items-center justify-between mb-2">
                                    <FaFileAlt className="text-3xl opacity-80" />
                                    <span className="text-sm opacity-90">Rejected</span>
                                </div>
                                <div className="text-4xl font-bold mb-1">
                                    {allApplications.filter(a => a.status === 'rejected').length}
                                </div>
                                <div className="text-sm opacity-90">Not Selected</div>
                            </div>
                        </div>

                        {/* Filters and Actions */}
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                                <div className="flex flex-wrap items-center gap-4">
                                    {/* Status Filter */}
                                    <div className="flex items-center gap-2">
                                        <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Status:</label>
                                        <select
                                            value={applicationFilters.status}
                                            onChange={(e) => setApplicationFilters(prev => ({ ...prev, status: e.target.value }))}
                                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                        >
                                            <option value="all">All Status</option>
                                            <option value="draft">Draft</option>
                                            <option value="submitted">Submitted</option>
                                            <option value="pending">Pending Review</option>
                                            <option value="under_review">Under Review</option>
                                            <option value="selected">Selected</option>
                                            <option value="rejected">Rejected</option>
                                            <option value="waitlisted">Waitlisted</option>
                                        </select>
                                    </div>

                                    {/* Village Filter */}
                                    <div className="flex items-center gap-2">
                                        <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Village:</label>
                                        <select
                                            value={applicationFilters.village}
                                            onChange={(e) => setApplicationFilters(prev => ({ ...prev, village: e.target.value }))}
                                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm min-w-[150px]"
                                        >
                                            <option value="all">All Villages</option>
                                            {uniqueVillages.map(village => (
                                                <option key={village} value={village}>{village}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                {/* Export Button */}
                                <button
                                    onClick={exportToCSV}
                                    disabled={filteredApplications.length === 0}
                                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium text-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
                                >
                                    <FaDownload /> Export to CSV ({filteredApplications.length})
                                </button>
                            </div>
                        </div>

                        {/* Applications List */}
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                            <div className="p-6 border-b border-gray-200">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                    <div>
                                        <h2 className="text-2xl font-bold text-gray-900">
                                            All Student Applications
                                        </h2>
                                        <p className="text-sm text-gray-600 mt-1">
                                            {filteredApplications.length} of {allApplications.length} applications
                                            {searchQuery && ` matching "${searchQuery}"`}
                                            {applicationFilters.status !== 'all' && ` • Status: ${applicationFilters.status}`}
                                            {applicationFilters.village !== 'all' && ` • Village: ${applicationFilters.village}`}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {tabLoading ? (
                                <div className="text-center py-12">
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                                    <p className="text-gray-600">Loading applications...</p>
                                </div>
                            ) : filteredApplications.length === 0 ? (
                                <div className="text-center py-12">
                                    <FaFileAlt className="text-5xl text-gray-300 mx-auto mb-4" />
                                    <p className="text-gray-600 text-lg font-medium mb-2">
                                        {searchQuery ? 'No applications found' : 'No applications yet'}
                                    </p>
                                    <p className="text-gray-500 text-sm">
                                        {searchQuery 
                                            ? 'Try adjusting your search criteria'
                                            : 'Applications will appear here once students start applying'}
                                    </p>
                                </div>
                            ) : (
                                <div className="divide-y divide-gray-200 max-h-[600px] overflow-y-auto custom-scrollbar">
                                    {filteredApplications.map((app) => (
                                        <Link
                                            key={app.id}
                                            to={`/applications/${app.id}/review`}
                                            className="block p-6 hover:bg-gray-50 transition-colors"
                                        >
                                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-3 mb-2">
                                                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                                                            <span className="text-blue-600 font-semibold text-sm">
                                                                {app.first_name?.charAt(0)}{app.last_name?.charAt(0)}
                                                            </span>
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <h3 className="font-semibold text-gray-900 text-lg">
                                                                {app.first_name} {app.last_name}
                                                            </h3>
                                                            <p className="text-sm text-gray-600 truncate">{app.email}</p>
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-gray-500">
                                                        {app.session_name && (
                                                            <span className="flex items-center gap-1">
                                                                <FaCalendarAlt /> {app.session_name}
                                                            </span>
                                                        )}
                                                        {app.address_village && (
                                                            <span className="flex items-center gap-1">
                                                                📍 {app.address_village}
                                                            </span>
                                                        )}
                                                        {app.submitted_at && (
                                                            <span>
                                                                Submitted: {new Date(app.submitted_at).toLocaleDateString()}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-4 flex-shrink-0">
                                                    <StatusBadge status={app.status} />
                                                    <FaArrowRight className="text-gray-400" />
                                                </div>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            )}

                            {filteredApplications.length > 0 && (
                                <div className="p-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
                                    <p className="text-center text-sm text-gray-600">
                                        Showing {filteredApplications.length} of {allApplications.length} applications
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {activeTab === 'donor-requests' && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-bold text-gray-900">Donor Requests</h2>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => loadTabData()}
                                    className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                                >
                                    Refresh
                                </button>
                            </div>
                        </div>
                        {tabLoading ? (
                            <div className="text-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
                                <p className="text-gray-600">Loading donor requests...</p>
                            </div>
                        ) : donorRequests.length === 0 ? (
                            <EmptyState
                                icon={FaHeart}
                                message="No donor requests found"
                            />
                        ) : (
                            <div className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                                    <div className="p-4 border border-gray-200 rounded-lg">
                                        <div className="text-sm text-gray-600 mb-1">Pending Requests</div>
                                        <div className="text-2xl font-bold text-yellow-600">
                                            {donorRequests.filter(r => r.status === 'pending').length}
                                        </div>
                                    </div>
                                    <div className="p-4 border border-gray-200 rounded-lg">
                                        <div className="text-sm text-gray-600 mb-1">Contacted</div>
                                        <div className="text-2xl font-bold text-blue-600">
                                            {donorRequests.filter(r => r.status === 'contacted').length}
                                        </div>
                                    </div>
                                    <div className="p-4 border border-gray-200 rounded-lg">
                                        <div className="text-sm text-gray-600 mb-1">Total Requests</div>
                                        <div className="text-2xl font-bold text-gray-900">
                                            {donorRequests.length}
                                        </div>
                                    </div>
                                </div>
                                <div className="space-y-3">
                                    {donorRequests.map((request) => (
                                        <div
                                            key={request.id}
                                            className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors"
                                        >
                                            <div className="flex items-start justify-between mb-3">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <h3 className="font-semibold text-gray-900">
                                                            {request.full_name}
                                                        </h3>
                                                        <StatusBadge 
                                                            status={request.status === 'pending' ? 'pending' : request.status === 'contacted' ? 'submitted' : request.status}
                                                        />
                                                        <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-700">
                                                            {request.request_type === 'individual_donor' ? 'Individual' : 'Corporate'}
                                                        </span>
                                                    </div>
                                                    <div className="text-sm text-gray-600 space-y-1">
                                                        <p><strong>Email:</strong> {request.email}</p>
                                                        {request.phone && <p><strong>Phone:</strong> {request.phone}</p>}
                                                        {request.organization_name && (
                                                            <p><strong>Organization:</strong> {request.organization_name}</p>
                                                        )}
                                                        {request.organization_type && (
                                                            <p><strong>Type:</strong> {request.organization_type}</p>
                                                        )}
                                                        {request.message && (
                                                            <p className="mt-2 text-gray-700"><strong>Message:</strong> {request.message}</p>
                                                        )}
                                                        <p className="text-xs text-gray-500 mt-2">
                                                            Submitted: {new Date(request.created_at).toLocaleDateString()}
                                                        </p>
                                                        {request.contacted_by_name && (
                                                            <p className="text-xs text-gray-500">
                                                                Contacted by: {request.contacted_by_name} {request.contacted_by_surname}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex gap-2 mt-4">
                                                {request.status === 'pending' && (
                                                    <button
                                                        onClick={async () => {
                                                            try {
                                                                await donorRequestAPI.updateStatus(request.id, 'contacted', '');
                                                                toast.success('Request marked as contacted');
                                                                loadTabData();
                                                            } catch (error) {
                                                                toast.error('Failed to update status');
                                                            }
                                                        }}
                                                        className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                                    >
                                                        Mark as Contacted
                                                    </button>
                                                )}
                                                <a
                                                    href={`mailto:${request.email}`}
                                                    className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                                >
                                                    Send Email
                                                </a>
                                                {request.phone && (
                                                    <a
                                                        href={`tel:${request.phone}`}
                                                        className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                                    >
                                                        Call
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'events' && (
                    <div className="space-y-6">
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                            <h2 className="text-xl font-bold text-gray-900 mb-1">{editingEventId ? 'Edit Event' : 'Create Event'}</h2>
                            <p className="text-sm text-gray-600 mb-6">Create rich event pages with banner image, detailed content, and gallery images.</p>
                            <form onSubmit={handleSubmitEvent} className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <input
                                        type="text"
                                        placeholder="Event title"
                                        value={eventForm.title}
                                        onChange={(e) => setEventForm(prev => ({ ...prev, title: e.target.value }))}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        required
                                    />
                                    <input
                                        type="date"
                                        value={eventForm.event_date}
                                        onChange={(e) => setEventForm(prev => ({ ...prev, event_date: e.target.value }))}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        required
                                    />
                                </div>
                                <input
                                    type="text"
                                    placeholder="Location (optional)"
                                    value={eventForm.event_location}
                                    onChange={(e) => setEventForm(prev => ({ ...prev, event_location: e.target.value }))}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                                <textarea
                                    placeholder="Event description"
                                    value={eventForm.description}
                                    onChange={(e) => setEventForm(prev => ({ ...prev, description: e.target.value }))}
                                    rows={4}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    required
                                />
                                <textarea
                                    placeholder="Event content (full story). Use empty lines between paragraphs."
                                    value={eventForm.content}
                                    onChange={(e) => setEventForm(prev => ({ ...prev, content: e.target.value }))}
                                    rows={6}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-600 mb-2">Intro media type (for event cards)</label>
                                        <select
                                            value={eventForm.media_type}
                                            onChange={(e) => setEventForm(prev => ({ ...prev, media_type: e.target.value }))}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        >
                                            <option value="none">No media</option>
                                            <option value="image">Image</option>
                                            <option value="video">Video</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-600 mb-2">Intro media file (homepage/news cards)</label>
                                        <input
                                            type="file"
                                            accept="image/*,video/*"
                                            onChange={(e) => setEventFile(e.target.files?.[0] || null)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-600 mb-2">Detail page banner image URL (optional)</label>
                                        <input
                                            type="text"
                                            inputMode="url"
                                            placeholder="https://... or /uploads/..."
                                            value={eventForm.banner_url}
                                            onChange={(e) => setEventForm(prev => ({ ...prev, banner_url: e.target.value }))}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-600 mb-2">Detail page banner image file</label>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) => setEventBannerFile(e.target.files?.[0] || null)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-3">
                                    <label className="block text-xs font-semibold text-gray-600">Gallery media (multiple images/videos) for blog section</label>
                                    <input
                                        type="file"
                                        accept="image/*,video/*,.mp4,.webm,.mov,.avi,.mkv"
                                        multiple
                                        onChange={handleGalleryFileSelection}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                                    />
                                    {eventGalleryFiles.length > 0 && (
                                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                                            <p className="text-xs font-semibold text-gray-700 mb-2">
                                                Pending uploads: {eventGalleryFiles.length} file(s)
                                            </p>
                                            <div className="flex flex-wrap gap-2">
                                                {eventGalleryFiles.map((file, index) => (
                                                    <button
                                                        key={`${file.name}-${file.size}-${index}`}
                                                        type="button"
                                                        onClick={() => removePendingGalleryFile(index)}
                                                        className="px-2 py-1 text-xs rounded bg-white border border-gray-200 text-gray-700 hover:bg-red-50 hover:text-red-600 hover:border-red-200"
                                                        title={`Remove ${file.name}`}
                                                    >
                                                        {file.name} x
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {eventForm.gallery_images.length > 0 && (
                                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                                            <p className="text-xs font-semibold text-gray-700 mb-2">
                                                Existing gallery media: {eventForm.gallery_images.length} file(s)
                                            </p>
                                            <div className="flex flex-wrap gap-2">
                                                {eventForm.gallery_images.map((img, index) => (
                                                    <button
                                                        key={`${img}-${index}`}
                                                        type="button"
                                                        onClick={() => setEventForm((prev) => ({
                                                            ...prev,
                                                            gallery_images: prev.gallery_images.filter((_, idx) => idx !== index)
                                                        }))}
                                                        className="px-2 py-1 text-xs rounded bg-white border border-gray-200 text-gray-700 hover:bg-red-50 hover:text-red-600 hover:border-red-200"
                                                    >
                                                        Remove media {index + 1}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <input
                                    type="url"
                                    placeholder="External video URL (optional)"
                                    value={eventForm.video_url}
                                    onChange={(e) => setEventForm(prev => ({ ...prev, video_url: e.target.value }))}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />

                                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                                    <input
                                        type="checkbox"
                                        checked={eventForm.is_published}
                                        onChange={(e) => setEventForm(prev => ({ ...prev, is_published: e.target.checked }))}
                                    />
                                    Publish immediately
                                </label>

                                <div className="flex gap-3">
                                    <button
                                        type="submit"
                                        disabled={savingEvent}
                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-300 transition-colors"
                                    >
                                        {savingEvent ? 'Saving...' : (editingEventId ? 'Update Event' : 'Create Event')}
                                    </button>
                                    {editingEventId && (
                                        <button
                                            type="button"
                                            onClick={resetEventForm}
                                            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                                        >
                                            Cancel Edit
                                        </button>
                                    )}
                                </div>
                            </form>
                        </div>

                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Published & Draft Events</h3>
                            {tabLoading ? (
                                <div className="text-center py-8">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
                                    <p className="text-gray-600">Loading events...</p>
                                </div>
                            ) : events.length === 0 ? (
                                <EmptyState message="No events created yet" />
                            ) : (
                                <div className="space-y-3">
                                    {events.map((event) => (
                                        <div key={event.id} className="border border-gray-200 rounded-lg p-4">
                                            <div className="flex items-start justify-between gap-4">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <h4 className="font-semibold text-gray-900">{event.title}</h4>
                                                        <StatusBadge status={event.is_published ? 'published' : 'draft'} />
                                                    </div>
                                                    <p className="text-sm text-gray-600 mb-2">{event.description}</p>
                                                    <p className="text-xs text-gray-500">
                                                        {new Date(event.event_date).toLocaleString()}
                                                        {event.event_location ? ` • ${event.event_location}` : ''}
                                                    </p>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        onClick={() => handleEditEvent(event)}
                                                        className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                                                        title="Edit event"
                                                    >
                                                        <FaEdit />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteEvent(event.id)}
                                                        className="p-2 text-red-600 hover:bg-red-50 rounded"
                                                        title="Delete event"
                                                    >
                                                        <FaTrash />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {activeTab === 'reports' && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h2 className="text-xl font-bold text-gray-900 mb-4">Reports & Analytics</h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="p-6 border border-gray-200 rounded-lg">
                                <h3 className="font-semibold text-gray-900 mb-2">Total Users</h3>
                                <p className="text-3xl font-bold text-blue-600 mb-1">{getTotalUsers()}</p>
                                <p className="text-sm text-gray-600">All registered users</p>
                            </div>
                            <div className="p-6 border border-gray-200 rounded-lg">
                                <h3 className="font-semibold text-gray-900 mb-2">Total Applications</h3>
                                <p className="text-3xl font-bold text-green-600 mb-1">{getTotalApplications()}</p>
                                <p className="text-sm text-gray-600">All time</p>
                            </div>
                            <div className="p-6 border border-gray-200 rounded-lg">
                                <h3 className="font-semibold text-gray-900 mb-2">Selection Rate</h3>
                                <p className="text-3xl font-bold text-purple-600 mb-1">
                                    {getTotalApplications() > 0 
                                        ? ((stats.applications?.find(a => a.status === 'selected')?.count || 0) / getTotalApplications() * 100).toFixed(1)
                                        : 0}%
                                </p>
                                <p className="text-sm text-gray-600">Selected vs Total</p>
                            </div>
                        </div>
                        <div className="mt-6">
                            <Link
                                to="/admin/reports"
                                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                            >
                                View Detailed Reports <FaArrowRight className="inline" />
                            </Link>
                        </div>
                    </div>
                )}

                {activeTab === 'settings' && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h2 className="text-xl font-bold text-gray-900 mb-4">System Settings</h2>
                        <div className="space-y-4">
                            <Link
                                to="/admin/settings"
                                className="block p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-all"
                            >
                                <h3 className="font-semibold text-gray-900 mb-2">General Settings</h3>
                                <p className="text-gray-600 text-sm">Configure system-wide settings and preferences</p>
                            </Link>
                            <Link
                                to="/admin/announcements"
                                className="block p-4 border border-gray-200 rounded-lg hover:border-green-300 hover:bg-green-50 transition-all"
                            >
                                <h3 className="font-semibold text-gray-900 mb-2">Announcement Management</h3>
                                <p className="text-gray-600 text-sm">Manage and create system announcements</p>
                            </Link>
                        </div>
                    </div>
                )}
        </DashboardLayout>
    );
};

export default AdminDashboard;
